// The lines a draft names, highlighted as the diff highlights them: the
// file is read at the draft's commit (through the bridge) and highlighted
// whole, so a line is parsed in its context. Files are kept per commit, the
// most recent few.
import { LruMap } from '../api/cache';
import { contentAt } from '../api/gh';
import { eng } from '../diff/engine';
import { renderLine } from '../logic/render';

/** One line of context: its number and its highlighted HTML. */
export interface ContextLine {
  n: number;
  html: string;
}

/** Most lines shown for one draft (the last ones of a longer range). */
export const CONTEXT_MAX = 8;

const files = new LruMap<string, Promise<{ lines: string[]; html: (i: number) => string } | null>>(40);

function file(owner: string, repo: string, path: string, commit: string) {
  const key = `${owner}/${repo}@${commit}:${path}`;
  let p = files.get(key);
  if (!p) {
    p = (async () => {
      const text = await contentAt(owner, repo, path, commit);
      if (text === null) return null;
      const lines = text.split('\n');
      const language = await eng.detectLanguage(path).catch(() => null);
      const spans = language ? await eng.highlight(language, text).then((h) => h.lines, () => null) : null;
      return { lines, html: (i: number) => renderLine(lines[i] ?? '', spans?.[i] ?? []) };
    })();
    p.catch(() => files.delete(key));
    files.set(key, p);
  }
  return p;
}

/**
 * Lines `from`–`to` (1-based, inclusive; at most CONTEXT_MAX, the last
 * ones) of `path` at `commit`, highlighted; null when the file cannot be
 * read there.
 */
export async function contextLines(owner: string, repo: string, path: string, commit: string, from: number, to: number): Promise<ContextLine[] | null> {
  const f = await file(owner, repo, path, commit).catch(() => null);
  if (!f) return null;
  const last = Math.min(to, f.lines.length);
  const first = Math.max(1, from, last - CONTEXT_MAX + 1);
  const out: ContextLine[] = [];
  for (let n = first; n <= last; n++) out.push({ n, html: f.html(n - 1) });
  return out;
}
